import cardContext from "./cardContext";
import React, { useState, useEffect, useRef } from 'react';
import menuService from '../../services/api/menuService';

const CardState = (props) => {
  const [cardData, setCardData] = useState([]);
  const [filteredCardData, setFilteredCardData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchMenuItems = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;
      
      const result = await menuService.getMenuItems();
      
      if (result.success && result.data && Array.isArray(result.data)) {
        const formattedData = result.data.map(item => {
          let optimizedImage = item.image;
          
          if (optimizedImage && optimizedImage.includes('unsplash.com') && !optimizedImage.includes('?')) {
            optimizedImage = `${optimizedImage}?w=240&h=240&q=80&fit=crop&auto=format`;
          } else if (optimizedImage && optimizedImage.includes('unsplash.com') && !optimizedImage.includes('w=240')) {
            const baseUrl = optimizedImage.split('?')[0];
            optimizedImage = `${baseUrl}?w=240&h=240&q=80&fit=crop&auto=format`;
          } else if (!optimizedImage) {
            optimizedImage = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=240&h=240&q=80&fit=crop&auto=format';
          }
          
          return {
            id: item.id,
            name: item.name,
            price: item.price,
            description: item.description,
            category: item.category.toLowerCase().replace(/\s+/g, '-'),
            image: optimizedImage,
            isVeg: item.veg || false
          };
        });
        
        setCardData(formattedData);
        setFilteredCardData(formattedData);
        
        preloadImages(formattedData.slice(0, 6));
      }
      
      setLoading(false);
    };

    fetchMenuItems();
  }, []);

  const preloadImages = (items) => {
    items.forEach(item => {
      const img = new Image();
      img.src = item.image;
    });
  };

  // Search functionality
  const handleSearch = (term) => {
    setSearchTerm(term);
    if (term.trim() === '') {
      setFilteredCardData(cardData); // Show all cards when search is empty
    } else {
      const filtered = cardData.filter(item =>
        item.name.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredCardData(filtered);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredCardData(cardData); // Restore all cards
  };

  const handleQuantityChange = (id, change) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max((prev[id] || 0) + change, 0),
    }));
  };

  const getTotalQuantity = () => {
    return Object.values(quantities).reduce((sum, quantity) => sum + quantity, 0);
  };

  const getSelectedCardIds = () => {
    return Object.entries(quantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([id]) => id);
  };

  const getCartItems = () => {
    return Object.entries(quantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([id, quantity]) => {
        const item = cardData.find(card => card.id.toString() === id); // Use full cardData, not filtered
        return {
          ...item,
          quantity
        };
      });
  };

  const updateQuantity = (id, newQuantity) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(newQuantity, 0),
    }));
  };

  const removeFromCart = (id) => {
    setQuantities((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const clearCart = () => {
    setQuantities({});
  };

  return (
    <cardContext.Provider value={{ 
      cardData: filteredCardData, 
      setCardData: setFilteredCardData, 
      searchTerm,
      handleSearch,
      clearSearch,
      getTotalQuantity, 
      getSelectedCardIds, 
      getCartItems, 
      handleQuantityChange, 
      updateQuantity,
      removeFromCart,
      clearCart,
      quantities,
      loading
    }}>
      {props.children}
    </cardContext.Provider>
  )
}

export default CardState;
